import React, { useRef, useState } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { startStripeCheckout } from '../../utils/upgradeFlow';
import { useAuth } from '../../hooks/useAuth';
import { useBugReport } from '../../context/BugReportContext';
import { AdminBadge } from '../Admin/ui';
import {
  ADMIN_DOMAINS,
  applyAdminLayoutToDomains,
  buildAdminTarget,
  roleCanAccessTool,
  toolIsAvailable,
} from '../Admin/adminNavConfig';
import { PARTNER_ADMIN_DOMAINS } from '../Admin/partnerAdminNavConfig';
import { useAdminLayout } from '../Admin/AdminLayoutContext';
import './UserActionBar.css';

// Public no-login shareable demo. Gated behind ONE all-user Share button in the
// action bar (same level as Bug Report).
//
// On the canonical prod domain we share the fixed public URL so a copied link
// works for any recipient regardless of where the sharer happens to be. On
// preprod / dev / local / Tailscale we share the CURRENT origin instead, so the
// Share button opens the build you are actually on (prod's /play is intentionally
// the older demo until this milestone ships — sharing the prod URL from preprod
// would open that stale version).
const SHARE_DEMO_PATH = '/play/hypertension';
const CANONICAL_DEMO_URL = `https://debono.ai${SHARE_DEMO_PATH}`;
function resolveShareDemoUrl() {
  if (typeof window === 'undefined' || !window.location) return CANONICAL_DEMO_URL;
  // Under the moth hub the app is mounted at PUBLIC_URL=/debono, so a bare
  // origin+/play/hypertension resolves to the hub root, not this app -> the share
  // button "does not lead to it". Prepend the mount (empty on root-served hosts,
  // e.g. prod, so this is a no-op there). Mirrors utils/publicPaths.js's stripMount.
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  return /(^|\.)debono\.ai$/i.test(window.location.hostname)
    ? CANONICAL_DEMO_URL
    : `${window.location.origin}${base}${SHARE_DEMO_PATH}`;
}

function filterDomainsByRole(domains, role = 'admin', nodeRole = null) {
  return domains
    .map((domain) => {
      // External-only domains (no tools, opens in new tab) bypass tool-role
      // filtering — gated only on admin role, applied at the caller via
      // isAdmin/showAdminWorkspaceNav.
      if (domain.external) {
        return { ...domain, tools: [] };
      }
      const tools = (domain.tools || []).filter((tool) => (
        (!tool.requires || roleCanAccessTool(tool, role)) && toolIsAvailable(tool, nodeRole)
      ));
      if (!tools.length) {
        return null;
      }
      return {
        ...domain,
        tools,
        defaultTool: tools.some((tool) => tool.id === domain.defaultTool) ? domain.defaultTool : tools[0].id,
      };
    })
    .filter(Boolean);
}

function resolveExternalUrl(url) {
  if (!url || typeof window === 'undefined') return url;
  if (url.includes('{host}')) {
    return url.replace('{host}', window.location.hostname);
  }
  return url;
}

function UserActionBar({
  variant = 'dashboard',
  onUpgrade,
  upcomingExams = [],
  activeExamId,
  onExamSwitch,
  naplexMode = false,
  ptcbMode = false,
  onExitExamReview,
  onAdmin,
  reckoningTab = null,
  onReckoningTabClick,
}) {
  const scrollRef = useRef(null);
  const [shareCopied, setShareCopied] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openBugReport } = useBugReport();
  const { layout, nodeRole } = useAdminLayout();
  const inExamReviewMode = naplexMode || ptcbMode;
  const isAdminRole = user?.role === 'admin' || user?.role === 'partner_admin';
  const isAdmin = isAdminRole;
  const adminBasePath = user?.role === 'partner_admin' ? '/partner-admin' : '/admin';
  const adminNavConfig = user?.role === 'partner_admin' ? PARTNER_ADMIN_DOMAINS : ADMIN_DOMAINS;
  const adminDomains = applyAdminLayoutToDomains(filterDomainsByRole(adminNavConfig, user?.role || 'admin', nodeRole), layout);
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/partner-admin');
  const showExamControls = inExamReviewMode || upcomingExams.length > 0;

  const getDaysUntil = (examDate) => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const dateStr = typeof examDate === 'string' ? examDate.split('T')[0] : examDate;
    const parts = typeof dateStr === 'string' ? dateStr.split('-').map(Number) : null;
    const date = parts && parts.length === 3
      ? new Date(parts[0], parts[1] - 1, parts[2])
      : new Date(examDate);
    date.setHours(0, 0, 0, 0);
    return Math.round((date - todayStart) / (1000 * 60 * 60 * 24));
  };

  const getUrgencyClass = (days) => (days <= 1 ? 'urgent' : days <= 2 ? 'soon' : 'normal');

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
      return;
    }
    startStripeCheckout('annual', { fallbackNavigate: navigate });
  };

  const handleAdminClick = () => {
    if (isAdminRoute) {
      navigate('/dashboard');
      return;
    }

    if (onAdmin) {
      onAdmin();
      return;
    }

    navigate(buildAdminTarget('review', 'inbox', null, adminBasePath));
  };

  const handleLogoutClick = () => {
    logout();
    navigate('/login');
  };

  const handleShareDemo = () => {
    // Open the no-login hypertension demo in a new tab AND copy the link so the user
    // can paste it to a colleague. Clipboard is best-effort (needs a user gesture +
    // secure context); the new tab opens regardless. Resolve the URL per host so
    // preprod shares preprod (canonical prod URL only on debono.ai).
    const shareUrl = resolveShareDemoUrl();
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 1800);
          })
          .catch(() => {});
      }
    } catch (_) {
      /* clipboard unavailable — the new tab still opens */
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const activeAdminDomain = isAdminRoute
    ? adminDomains.find((domain) => location.pathname === `${adminBasePath}/${domain.key}`) || null
    : null;
  const activeAdminTool = activeAdminDomain
    ? activeAdminDomain.tools.find((tool) => tool.id === new URLSearchParams(location.search).get('tool')) || null
    : null;
  const showAdminWorkspaceNav = variant === 'admin' && isAdmin;

  return (
    <div className={`exam-tabs-bar${variant === 'admin' ? ' exam-tabs-bar--admin' : ''}`}>
      {showAdminWorkspaceNav && (
        <nav className="eab-admin-workspace-nav" aria-label="Admin workspaces">
          {adminDomains.map((domain) => {
            if (domain.external) {
              return (
                <a
                  key={domain.key}
                  href={resolveExternalUrl(domain.external)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="eab-workspace-pill eab-workspace-pill--external"
                  title={domain.description || `Opens ${domain.external} in a new tab`}
                >
                  <span className="eab-workspace-pill__label">{domain.label}</span>
                  <span className="eab-workspace-pill__external-glyph" aria-hidden="true">↗</span>
                </a>
              );
            }
            return (
              <NavLink
                key={domain.key}
                to={buildAdminTarget(domain.key, domain.defaultTool, null, adminBasePath)}
                className={`eab-workspace-pill${activeAdminDomain?.key === domain.key ? ' is-active' : ''}`}
                title={domain.description}
              >
                <span className="eab-workspace-pill__label">{domain.label}</span>
                {activeAdminDomain?.key === domain.key && activeAdminTool?.beta ? (
                  <AdminBadge tone="ambient" className="eab-workspace-pill__badge">BETA</AdminBadge>
                ) : null}
              </NavLink>
            );
          })}
        </nav>
      )}
      {!showAdminWorkspaceNav && (showExamControls || reckoningTab) && (
        <div className="exam-tabs-scroll" ref={scrollRef}>
          {reckoningTab && (
            <button
              key="reckoning-tab"
              type="button"
              className={`exam-pill exam-pill--reckoning${reckoningTab.active ? ' active' : ''}`}
              onClick={onReckoningTabClick}
            >
              <span className="exam-pill-title">Reckoning</span>
              <span className="exam-pill-countdown exam-pill-countdown--reckoning">
                {reckoningTab.allCleared ? 'Boss' : `${reckoningTab.cleared}/${reckoningTab.total}`}
              </span>
            </button>
          )}
          {/* The Reckoning tab is navigation only — abandoning a reckoning lives
              in the ContentTray (ct-orb--danger "Abandon Reckoning"), the same
              place exam deletion lives. No delete control belongs in this bar. */}
          {upcomingExams.map((exam) => {
            const days = getDaysUntil(exam.exam_date);
            const urgency = getUrgencyClass(days);
            // When the Reckoning tab is the active selection, activeExamId still
            // resolves to the soonest exam (it has no null state), which would
            // light THIS pill too — so the bar showed both Reckoning and an exam
            // selected at once. Selection is mutually exclusive: suppress the
            // exam-pill active state whenever Reckoning is active.
            const isActive = exam.id === activeExamId && !inExamReviewMode && !reckoningTab?.active;
            const isRevived = exam.revived;
            const isCompleted = !!exam.completed_at && !isRevived;

            return (
              <button
                key={exam.id}
                className={`exam-pill ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => {
                  // While in a DLC review map, clicking any exam pill should
                  // drop back to the exam map first — the pill IS the exit.
                  if (inExamReviewMode && onExitExamReview) {
                    onExitExamReview();
                  }
                  onExamSwitch?.(exam.id);
                }}
              >
                <span className="exam-pill-title">{exam.title}</span>
                {isCompleted ? (
                  <span className="exam-pill-countdown countdown-completed">Done</span>
                ) : isRevived ? (
                  <span className="exam-pill-countdown countdown-revived">Revived</span>
                ) : (
                  <span className={`exam-pill-countdown countdown-${urgency}`}>{days}d</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="exam-tabs-actions">
        {user && (
          user.subscription_tier === 'base' ? (
            <button className="upgrade-button" onClick={handleUpgrade} aria-label="Upgrade to Premium">Upgrade</button>
          ) : (
            <span className="premium-badge">Premium</span>
          )
        )}
        <button className="help-codex-nav-button" onClick={() => window.dispatchEvent(new CustomEvent('open-help-codex'))} aria-label="Feature guide">
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'inherit' }}>?</span>
        </button>
        <button className="feature-request-nav-button" onClick={() => window.dispatchEvent(new CustomEvent('open-feature-modal'))} aria-label="Request a feature">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
        {user?.role === 'admin' && (
          <a
            href="http://[fd7a:115c:a1e0::1701:473f]:3050/"
            target="_blank"
            rel="noopener noreferrer"
            className="dev-portal-nav-button"
            aria-label="Open partner-trusted Debono dev environment"
            title="Dev Portal — partner-trusted dev (port 3050, tailnet-only)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="2,12 6,12 8,8 11,16 13,4 15,20 17,12 22,12" />
            </svg>
          </a>
        )}
        <button
          className="share-demo-nav-button"
          onClick={handleShareDemo}
          aria-label="Share the free hypertension demo, opens in a new tab and copies the link"
          title={shareCopied ? 'Link copied!' : 'Share the free hypertension demo'}
        >
          {shareCopied ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          )}
        </button>
        <button className="bug-report-nav-button" onClick={() => openBugReport()} aria-label="Report a bug">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2l1.88 1.88" />
            <path d="M14.12 3.88L16 2" />
            <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
            <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
            <path d="M12 20v-9" />
            <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
            <path d="M6 13H2" />
            <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
            <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
            <path d="M22 13h-4" />
            <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
          </svg>
        </button>
        {user?.developer_dev_port && (
          <a
            href={`https://kyle-dev.phillyshell.cloud/`}
            target="_blank"
            rel="noopener noreferrer"
            className="developer-dev-button"
            aria-label="Open your dev environment"
            title="Open your dev environment (kyle-dev.phillyshell.cloud)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </a>
        )}
        <button className="settings-nav-button" onClick={() => window.dispatchEvent(new CustomEvent('open-settings-modal'))} aria-label="Settings">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
        {isAdminRole && (
          <button
            className={`admin-nav-button${isAdminRoute ? ' admin-nav-button--active' : ''}`}
            onClick={handleAdminClick}
            aria-label={isAdminRoute ? 'Back to Dashboard' : 'Admin Panel'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 15v2" />
              <path d="M12 3v4" />
              <rect x="4" y="7" width="16" height="10" rx="2" />
              <path d="M8 7V5a4 4 0 0 1 8 0v2" />
            </svg>
          </button>
        )}
        <button className="logout-button" onClick={handleLogoutClick} aria-label="Log out">
          <div className="logout-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
}

export default UserActionBar;
